import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Home, Search } from "lucide-react";
import { Link } from "react-router";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

export function meta() {
    return [
        { title: "Page Not Found | Kwik Nkap" },
        {
            name: "description",
            content: "We couldn't find the page you're looking for.",
        },
    ];
}

// Splat (catch-all) route. Mirrors the Next.js `not-found.tsx`, which simply
// rendered the not-found page. The global error UI for thrown errors is
// provided by the root `ErrorBoundary` in `app/root.tsx`.
export default function CatchAllNotFound() {
    const prefersReduced = useReducedMotion();
    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
            <motion.div
                initial={{ opacity: 0, y: prefersReduced ? 0 : 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                    duration: prefersReduced ? 0 : 0.6,
                    ease: [0.16, 1, 0.3, 1],
                }}
                className="w-full max-w-md"
            >
                <Card className="border-primary/20 shadow-lg">
                    <CardHeader className="space-y-1 pb-2">
                        <div className="flex items-center justify-center mb-2">
                            <motion.div
                                initial={{ scale: prefersReduced ? 1 : 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: prefersReduced ? 0 : 0.2,
                                }}
                            >
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Search className="h-8 w-8 text-primary" />
                                </div>
                            </motion.div>
                        </div>
                        <CardTitle className="text-center text-2xl font-bold">
                            Page Not Found
                        </CardTitle>
                        <CardDescription className="text-center">
                            We couldn't find the page you're looking for
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-0">
                        <div className="flex justify-center py-6">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{
                                    delay: 0.3,
                                    duration: prefersReduced ? 0 : 0.7,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                            >
                                <div className="text-8xl font-bold text-primary/20">404</div>
                            </motion.div>
                        </div>

                        <div className="space-y-2 text-sm text-muted-foreground">
                            <p>Possible reasons:</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li>The page may have been moved or deleted</li>
                                <li>You might have typed the address incorrectly</li>
                                <li>The link you followed may be outdated</li>
                            </ul>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row gap-3">
                        <Link to="/" className={buttonVariants({ variant: "default" })}>
                            <Home className="mr-2 h-4 w-4" />
                            Go to Homepage
                        </Link>

                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => window.history.back()}
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Go Back
                        </Button>
                    </CardFooter>
                </Card>
            </motion.div>
        </div>
    );
}
